import { type ColumnDef, type ExpandedState, flexRender, getCoreRowModel, getExpandedRowModel, type OnChangeFn, type PaginationState, type Row, type SortingState, useReactTable, } from "@tanstack/react-table";
import { Fragment, type ReactNode } from "react";
import classNames from "classnames";
import { Text } from "../typography";
import { Pagination } from "../pagination";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
export type TableProps<T> = {
    data: T[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: ColumnDef<T, any>[];
    loading: boolean;
    pagination?: PaginationState;
    setPagination?: OnChangeFn<PaginationState>;
    sorting?: SortingState;
    setSorting?: OnChangeFn<SortingState>;
    expanded?: ExpandedState;
    setExpanded?: OnChangeFn<ExpandedState>;
    pageCount?: number;
    getRowCanExpand?: (row: Row<T>) => boolean;
    renderSubComponent?: (row: Row<T>) => ReactNode;
    excludedColumns?: string[];
    emptyMessage?: string;
    onRowClick?: (row: Row<T>) => void;
    showHeader?: boolean;
};
export function Table<T>({ data, columns, pageCount, loading, pagination, setPagination, setSorting, sorting, expanded, setExpanded, getRowCanExpand, renderSubComponent, excludedColumns = [], emptyMessage = "No data", onRowClick, showHeader = true, }: TableProps<T>) {
    const filteredColumns = columns.filter((column) => !excludedColumns.includes(column.id as string));
    const table = useReactTable<T>({
        data,
        columns: filteredColumns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        paginateExpandedRows: true,
        manualPagination: true,
        manualSorting: true,
        pageCount,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onExpandedChange: setExpanded,
        getRowCanExpand,
        state: {
            pagination,
            sorting,
            expanded,
        },
    });
    if (!loading && (!data || data.length === 0)) {
        return (<div className="flex items-center justify-center w-full py-8 min-h-[400px]">
        <Text variant="bodySm" tone="body">
          {emptyMessage}
        </Text>
      </div>);
    }
    // Shimmer skeleton rows for loading state
    // Use consistent widths per row to avoid re-renders
    const shimmerWidths = [75, 85, 70, 90, 65, 80];
    const shimmerRows = Array.from({ length: 5 }).map((_, idx) => (<tr key={`shimmer-${idx}`} className="bg-uiSecondary rounded-xl h-12 sm:h-[54px] lg:h-[48px] my-2">
      {filteredColumns.map((_, colIdx, array) => (<td key={`shimmer-cell-${idx}-${colIdx}`} className={classNames("px-3 sm:px-4 lg:px-4 py-2", colIdx === 0 ? "rounded-l-xl" : "", colIdx === array.length - 1 ? "rounded-r-xl" : "")}>
          <div className="h-4 shimmer rounded" style={{
                width: `${shimmerWidths[(idx + colIdx) % shimmerWidths.length]}%`,
            }}/>
        </td>))}
    </tr>));
    return (<div className="flow-root min-w-0">
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-y-2">
          {showHeader && (<thead>
              {table.getHeaderGroups().map((headerGroup) => (<tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (<th key={header.id} className={`py-0 pl-4 text-left${index === 0 ? "pr-3" : "pr-3"}`}>
                      {header.isPlaceholder ? null : (<div {...{
                        role: header.column.getCanSort() ? "button" : "",
                        className: header.column.getCanSort()
                            ? "select-none cursor-pointer flex items-center gap-1"
                            : "flex items-center gap-1",
                        onClick: header.column.getToggleSortingHandler(),
                    }}>
                          <Text variant="bodyLg" tone="dark">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </Text>
                          {{
                            asc: <IconChevronDown className="h-4 w-4"/>,
                            desc: <IconChevronUp className="h-4 w-4"/>,
                        }[header.column.getIsSorted() as string] ?? null}
                        </div>)}
                    </th>))}
                </tr>))}
            </thead>)}
          <tbody>
            {loading
            ? shimmerRows
            : table.getRowModel().rows.map((row) => (<Fragment key={row.id}>
                    <tr className="group relative cursor-pointer my-2 hover:z-10 bg-uiSecondary hover:opacity-80 transition-opacity rounded-xl h-12 sm:h-[54px] lg:h-[48px]" onClick={() => {
                    if (onRowClick) {
                        onRowClick(row);
                    }
                    else if (getRowCanExpand?.(row)) {
                        table.setExpanded({ [row.id]: !row.getIsExpanded() });
                    }
                }}>
                      {row.getVisibleCells().map((cell, index, array) => (<td key={cell.id} className={classNames("px-3 sm:px-4 lg:px-4 py-2", index === 0 ? "rounded-l-xl" : "", index === array.length - 1 ? "rounded-r-xl" : "")}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>))}
                    </tr>

                    {row.getIsExpanded() && renderSubComponent && (<tr>
                        <td colSpan={columns.length} className="rounded-xl">
                          {renderSubComponent(row)}
                        </td>
                      </tr>)}
                  </Fragment>))}
          </tbody>
        </table>

        {!loading && pageCount && pageCount > 1 && (<div className="mt-4 flex justify-center">
            <Pagination page={pagination!.pageIndex + 1} pageCount={pageCount} onPageChange={(page) => table.setPageIndex(page - 1)}/>
          </div>)}
      </div>

      <div className="block md:hidden space-y-6">
        {loading
            ? Array.from({ length: 5 }).map((_, idx) => (<div key={`shimmer-mobile-${idx}`} className="bg-uiSecondary rounded-lg p-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {filteredColumns
                    .filter((col) => col.id !== "actions")
                    .map((_, colIdx) => {
                    const widths = [75, 85, 70, 90, 65, 80];
                    return (<div key={`shimmer-mobile-cell-${idx}-${colIdx}`} className="flex flex-col w-full">
                          <div className="h-3 shimmer rounded mb-2" style={{ width: "60%" }}/>
                          <div className="h-4 shimmer rounded" style={{
                            width: `${widths[(idx + colIdx) % widths.length]}%`,
                        }}/>
                        </div>);
                })}
                </div>
              </div>))
            : table.getRowModel().rows.map((row) => (<div key={row.id} className="group bg-uiSecondary rounded-lg p-4 relative isolate cursor-pointer hover:opacity-80 transition-opacity" onClick={() => {
                    if (onRowClick) {
                        onRowClick(row);
                    }
                }}>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {row
                    .getVisibleCells()
                    .filter((cell) => cell.column.id !== "actions")
                    .map((cell) => (<div key={cell.id} className="flex min-w-0 flex-col w-full">
                        <Text variant="bodyXs" tone="body" className="uppercase">
                          {cell.column.columnDef.header as string}
                        </Text>
                        <Text variant="bodySm" tone="dark" className="flex justify-between items-center break-words" as="div">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Text>
                      </div>))}
                </div>
                {/* Actions row for mobile */}
                <div className="flex justify-center mt-4 pt-4 border-t border-uiSecondaryDisabled/20">
                  {row
                    .getVisibleCells()
                    .find((cell) => cell.column.id === "actions") && (<div className="flex items-center gap-2">
                      <Text variant="bodyXs" tone="body" className="uppercase mr-2">
                        Details:
                      </Text>
                      {flexRender(row
                        .getVisibleCells()
                        .find((cell) => cell.column.id === "actions")!.column
                        .columnDef.cell, row
                        .getVisibleCells()
                        .find((cell) => cell.column.id === "actions")!
                        .getContext())}
                    </div>)}
                </div>
              </div>))}

        {/* Mobile pagination */}
        {!loading && pageCount && pageCount > 1 && (<div className="mt-3 flex justify-center">
            <Pagination page={pagination!.pageIndex + 1} pageCount={pageCount} onPageChange={(page) => table.setPageIndex(page - 1)}/>
          </div>)}
      </div>
    </div>);
}
